import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { api, formatApiErrorDetail, fmtCurrency, fmtNumber } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { useAIContext } from "@/context/AIContextProvider";
import { TID } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ShieldAlert, Users, Building2, Layers, Briefcase, FileText, Share2,
  Search, Plus, TrendingUp, Loader2, ArrowLeft,
} from "lucide-react";

const TIER_LABEL = { share_link: "Share Link", full_portal: "Full Portal" };

function TierBadge({ tier }) {
  const isFull = tier === "full_portal";
  return (
    <Badge className={isFull
      ? "bg-gbe text-white hover:bg-gbe/90"
      : "bg-gbe-accent text-gbe hover:bg-gbe-accent/90"}>
      {TIER_LABEL[tier] || tier}
    </Badge>
  );
}

function StatTile({ label, value, icon: Icon }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </div>
      <div className="mt-1.5 font-display text-2xl text-slate-900">{value}</div>
    </div>
  );
}

export default function CRMMasterPage() {
  const { can, loaded } = usePermissions();
  const { setContext } = useAIContext();
  const [clients, setClients] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [graph, setGraph] = useState(null);
  const [graphBusy, setGraphBusy] = useState(false);

  // create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newTier, setNewTier] = useState("share_link");
  const [newIndustry, setNewIndustry] = useState("");
  const [newContactEmail, setNewContactEmail] = useState("");
  const [creating, setCreating] = useState(false);

  // quick-create sub-entity: project / job / invoice
  const [subKind, setSubKind] = useState(null);  // "project" | "job" | "invoice"
  const [subForm, setSubForm] = useState({});
  const [subBusy, setSubBusy] = useState(false);

  const openSub = (kind) => {
    setSubKind(kind);
    setSubForm(kind === "invoice"
      ? { invoice_number: "", amount_ex_tax: "", tax: "", status: "draft" }
      : kind === "job"
        ? { name: "", status: "scheduled", scheduled_date: "" }
        : { name: "", status: "active", budget: "" });
  };
  const closeSub = () => { setSubKind(null); setSubForm({}); };

  const submitSub = async () => {
    if (!selected) return;
    setSubBusy(true);
    try {
      const path = subKind === "project" ? "projects" : subKind === "job" ? "jobs" : "invoices";
      // Strip empty strings so Pydantic doesn't fail on optional numeric fields
      const clean = Object.fromEntries(
        Object.entries(subForm).filter(([, v]) => v !== "" && v !== null && v !== undefined)
      );
      const payload = { ...clean, client_id: selected.id };
      if (subKind === "invoice") {
        payload.amount_ex_tax = parseFloat(payload.amount_ex_tax || 0);
        payload.tax = parseFloat(payload.tax || 0);
      } else if (subKind === "project" && payload.budget !== undefined) {
        payload.budget = parseFloat(payload.budget);
      }
      await api.post(`/master/${path}`, payload);
      toast.success(`${subKind[0].toUpperCase() + subKind.slice(1)} created`);
      closeSub();
      openDetail(selected);   // refresh graph
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubBusy(false);
    }
  };

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/master/clients");
      setClients(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (loaded && can("crm", "view")) refresh(); /* eslint-disable-next-line */ }, [loaded]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return clients;
    return clients.filter((c) => (c.name || "").toLowerCase().includes(s));
  }, [clients, q]);

  const VISIBLE = 300;
  const shown = useMemo(() => filtered.slice(0, VISIBLE), [filtered]);

  const openDetail = async (client) => {
    setSelected(client);
    setGraph(null);
    setGraphBusy(true);
    try {
      const { data } = await api.get(`/master/graph/client/${client.id}`);
      setGraph(data);
      // Push into AI context so the assistant knows what the user is looking at
      setContext({
        module: "crm",
        summary: `Client "${data.client?.name}" (${data.client?.portal_tier})`,
        counts: data.counts,
        client: {
          id: data.client?.id, name: data.client?.name,
          portal_tier: data.client?.portal_tier, industry: data.client?.industry,
          primary_contact_email: data.client?.primary_contact_email,
        },
      });
    } catch (e) {
      toast.error("Failed to load client details");
    } finally {
      setGraphBusy(false);
    }
  };

  const closeDetail = () => { setSelected(null); setGraph(null); setContext(null); };

  const create = async () => {
    if (!newName.trim()) { toast.error("Client name is required"); return; }
    setCreating(true);
    try {
      await api.post("/master/clients", {
        name: newName.trim(),
        portal_tier: newTier,
        industry: newIndustry || null,
        primary_contact_email: newContactEmail || null,
      });
      toast.success("Client created");
      setCreateOpen(false);
      setNewName(""); setNewIndustry(""); setNewContactEmail(""); setNewTier("share_link");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  if (!loaded) return <AppLayout title="CRM"><div className="h-64 rounded-lg bg-white border border-slate-200 shimmer" /></AppLayout>;

  if (!can("crm", "view")) {
    return (
      <AppLayout title="CRM · Master Data"
        subtitle="Create clients, projects, jobs and invoices (legacy master-data view)">
        <div className="max-w-md mx-auto mt-16 text-center">
          <div className="w-14 h-14 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-6 h-6 text-rose-700" />
          </div>
          <h2 className="font-display text-xl font-semibold text-slate-900">Access restricted</h2>
          <p className="text-slate-600 mt-2 text-sm">Your role does not have access to CRM.</p>
        </div>
      </AppLayout>
    );
  }

  const canCreate = can("crm", "create");

  return (
    <AppLayout title="CRM · Master Data" subtitle="Clients · Sites · Buildings · Projects · Jobs · Invoices">
      <div className="flex items-center gap-3 mb-4">
        <Button asChild variant="outline" size="sm" data-testid="crm-master-back-btn" className="shrink-0">
          <Link to="/crm"><ArrowLeft className="w-4 h-4 mr-1.5" /> Back to CRM</Link>
        </Button>
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid={TID.crmSearch}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search clients…"
            className="pl-9 h-9 bg-white"
          />
        </div>
        <div className="ml-auto text-xs text-slate-500">{fmtNumber(filtered.length)} clients</div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[11px] uppercase tracking-wider text-white bg-gbe">
          <div className="col-span-4">Client</div>
          <div className="col-span-2">Portal Tier</div>
          <div className="col-span-2 text-right">Sites</div>
          <div className="col-span-2 text-right">Assets</div>
          <div className="col-span-2 text-right">Projects</div>
        </div>
        <div className="divide-y divide-slate-100">
          {busy && <div className="p-10 text-center text-sm text-slate-500"><Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading…</div>}
          {!busy && filtered.length === 0 && clients.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-500">
              <div>No clients visible to you.</div>
              <div className="text-xs text-slate-400 mt-1">Field workers see clients they have assigned jobs on. Ask your manager if you expect to see one.</div>
            </div>
          )}
          {!busy && filtered.length === 0 && clients.length > 0 && (
            <div className="p-10 text-center text-sm text-slate-500">No clients match your search.</div>
          )}
          {shown.map((c) => (
            <button
              key={c.id}
              data-testid={TID.crmClientRow(c.id)}
              onClick={() => openDetail(c)}
              className="w-full grid grid-cols-12 gap-2 items-center px-4 py-3 text-sm hover:bg-slate-50 transition-colors text-left"
            >
              <div className="col-span-4">
                <div className="font-medium text-slate-900">{c.name}</div>
                {c.industry && <div className="text-xs text-slate-500">{c.industry}</div>}
              </div>
              <div className="col-span-2"><TierBadge tier={c.portal_tier} /></div>
              <div className="col-span-2 text-right tabular-nums text-slate-700">{fmtNumber(c.site_count)}</div>
              <div className="col-span-2 text-right tabular-nums text-slate-700">{fmtNumber(c.asset_count)}</div>
              <div className="col-span-2 text-right tabular-nums text-slate-700">{fmtNumber(c.project_count)}</div>
            </button>
          ))}
          {!busy && filtered.length > VISIBLE && (
            <div className="p-4 text-center text-xs text-slate-500 bg-slate-50">
              Showing first {fmtNumber(VISIBLE)} of {fmtNumber(filtered.length)} clients — type in the search box to find any client.
            </div>
          )}
        </div>
      </div>

      {/* Detail sheet */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && closeDetail()}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-display text-xl text-gbe">
              {selected?.name}
            </SheetTitle>
            <div className="flex items-center gap-2 mt-1">
              <TierBadge tier={selected?.portal_tier} />
              {selected?.industry && <span className="text-xs text-slate-500">{selected.industry}</span>}
            </div>
          </SheetHeader>

          {graphBusy && <div className="p-10 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>}
          {graph && (
            <div className="mt-4">
              <Tabs defaultValue="overview">
                <TabsList className="bg-slate-50 border border-slate-200 p-1 h-auto">
                  <TabsTrigger data-testid={TID.crmTab("overview")} value="overview" className="text-xs data-[state=active]:bg-white">Overview</TabsTrigger>
                  <TabsTrigger data-testid={TID.crmTab("sites")} value="sites" className="text-xs data-[state=active]:bg-white">Sites ({graph.counts.sites})</TabsTrigger>
                  <TabsTrigger data-testid={TID.crmTab("projects")} value="projects" className="text-xs data-[state=active]:bg-white">Projects ({graph.counts.projects})</TabsTrigger>
                  <TabsTrigger data-testid={TID.crmTab("jobs")} value="jobs" className="text-xs data-[state=active]:bg-white">Jobs ({graph.counts.jobs})</TabsTrigger>
                  <TabsTrigger data-testid={TID.crmTab("invoices")} value="invoices" className="text-xs data-[state=active]:bg-white">Invoices ({graph.counts.invoices})</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="mt-4">
                  <div className="grid grid-cols-3 gap-3">
                    <StatTile label="Sites" value={fmtNumber(graph.counts.sites)} icon={Building2} />
                    <StatTile label="Buildings" value={fmtNumber(graph.counts.buildings)} icon={Layers} />
                    <StatTile label="Areas" value={fmtNumber(graph.counts.areas)} icon={Layers} />
                    <StatTile label="Assets" value={fmtNumber(graph.counts.assets)} icon={Building2} />
                    <StatTile label="Projects" value={fmtNumber(graph.counts.projects)} icon={TrendingUp} />
                    <StatTile label="Jobs" value={fmtNumber(graph.counts.jobs)} icon={Briefcase} />
                  </div>
                  <div className="mt-6 rounded-lg border border-slate-200 bg-white p-4 text-sm">
                    <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Contact</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-xs text-slate-500">Primary contact</div>
                        <div>{graph.client.primary_contact_name || "—"}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Email</div>
                        <div>{graph.client.primary_contact_email || "—"}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Phone</div>
                        <div>{graph.client.primary_contact_phone || "—"}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">ABN</div>
                        <div>{graph.client.abn || "—"}</div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="sites" className="mt-4">
                  <SimpleTable
                    rows={graph.sites}
                    cols={[["name", "Site"], ["address", "Address"], ["code", "Code"]]}
                    empty="No sites yet"
                  />
                </TabsContent>
                <TabsContent value="projects" className="mt-4">
                  <SimpleTable
                    rows={graph.projects}
                    cols={[["name", "Project"], ["status", "Status"], ["budget", "Budget", (v) => fmtCurrency(v)], ["start_date", "Start"]]}
                    empty="No projects yet"
                  />
                </TabsContent>
                <TabsContent value="jobs" className="mt-4">
                  <SimpleTable
                    rows={graph.jobs}
                    cols={[["name", "Job"], ["status", "Status"], ["scheduled_date", "Scheduled"], ["code", "Code"]]}
                    empty="No jobs yet"
                  />
                </TabsContent>
                <TabsContent value="invoices" className="mt-4">
                  <SimpleTable
                    rows={graph.invoices}
                    cols={[["invoice_number", "#"], ["status", "Status"], ["total", "Total", (v) => fmtCurrency(v)], ["due_date", "Due"]]}
                    empty="No invoices yet"
                  />
                </TabsContent>
              </Tabs>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Client</DialogTitle>
            <DialogDescription>Add a client to the CRM. Portal tier controls how they view their data.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-700">Client name *</label>
              <Input data-testid={TID.crmCreateName} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. St Catherine's College" className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Portal Tier</label>
              <Select value={newTier} onValueChange={setNewTier}>
                <SelectTrigger data-testid={TID.crmCreatePortalTier} className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="share_link">Share Link (one-off reports)</SelectItem>
                  <SelectItem value="full_portal">Full Portal (login access)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Industry</label>
              <Input value={newIndustry} onChange={(e) => setNewIndustry(e.target.value)} placeholder="e.g. Education" className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Primary contact email</label>
              <Input value={newContactEmail} onChange={(e) => setNewContactEmail(e.target.value)} placeholder="name@client.com" className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button data-testid={TID.crmCreateSubmit} disabled={creating} onClick={create} className="bg-gbe hover:bg-gbe/90 text-white">
              {creating ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating…</> : "Create client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sub-entity quick-create dialog (project / job / invoice) */}
      <Dialog open={!!subKind} onOpenChange={(o) => !o && closeSub()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New {subKind && subKind[0].toUpperCase() + subKind.slice(1)}</DialogTitle>
            <DialogDescription>Attach a new {subKind} to {selected?.name}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {subKind === "invoice" ? (
              <>
                <div>
                  <label className="text-xs font-medium text-slate-700">Invoice number *</label>
                  <Input data-testid="sub-invoice-number" value={subForm.invoice_number || ""} onChange={(e) => setSubForm({ ...subForm, invoice_number: e.target.value })} placeholder="INV-1001" className="mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-700">Amount (ex tax) *</label>
                    <Input value={subForm.amount_ex_tax || ""} onChange={(e) => setSubForm({ ...subForm, amount_ex_tax: e.target.value })} placeholder="1000" type="number" className="mt-1" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-700">Tax</label>
                    <Input value={subForm.tax || ""} onChange={(e) => setSubForm({ ...subForm, tax: e.target.value })} placeholder="100" type="number" className="mt-1" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700">Status</label>
                  <Select value={subForm.status || "draft"} onValueChange={(v) => setSubForm({ ...subForm, status: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["draft", "submitted", "approved", "paid", "overdue", "void"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="text-xs font-medium text-slate-700">{subKind === "job" ? "Job" : "Project"} name *</label>
                  <Input data-testid="sub-name-input" value={subForm.name || ""} onChange={(e) => setSubForm({ ...subForm, name: e.target.value })}
                         placeholder={subKind === "job" ? "e.g. Quarterly compliance check" : "e.g. FY26 Renewal Programme"} className="mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-700">Status</label>
                    <Select value={subForm.status || (subKind === "job" ? "scheduled" : "active")}
                            onValueChange={(v) => setSubForm({ ...subForm, status: v })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(subKind === "job"
                            ? ["scheduled", "in_progress", "complete", "cancelled"]
                            : ["planning", "active", "on_hold", "complete", "cancelled"]
                          ).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-700">{subKind === "job" ? "Scheduled date" : "Budget ($)"}</label>
                    {subKind === "job" ? (
                      <Input type="date" value={subForm.scheduled_date || ""} onChange={(e) => setSubForm({ ...subForm, scheduled_date: e.target.value })} className="mt-1" />
                    ) : (
                      <Input type="number" value={subForm.budget || ""} onChange={(e) => setSubForm({ ...subForm, budget: e.target.value })} placeholder="50000" className="mt-1" />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeSub}>Cancel</Button>
            <Button data-testid="sub-submit-button" disabled={subBusy} onClick={submitSub} className="bg-gbe hover:bg-gbe/90 text-white">
              {subBusy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating…</> : `Create ${subKind || ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

function SimpleTable({ rows, cols, empty }) {
  if (!rows || rows.length === 0) {
    return <div className="p-6 text-center text-sm text-slate-500 rounded-lg border border-dashed border-slate-200">{empty}</div>;
  }
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="grid gap-2 px-4 py-2 text-[11px] uppercase tracking-wider text-white bg-gbe"
           style={{ gridTemplateColumns: `repeat(${cols.length}, minmax(0,1fr))` }}>
        {cols.map(([k, label]) => <div key={k}>{label}</div>)}
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map((r) => (
          <div key={r.id} className="grid gap-2 px-4 py-2.5 text-sm text-slate-700"
               style={{ gridTemplateColumns: `repeat(${cols.length}, minmax(0,1fr))` }}>
            {cols.map(([k, , fmt]) => <div key={k} className="truncate">{fmt ? fmt(r[k]) : (r[k] ?? "—")}</div>)}
          </div>
        ))}
      </div>
    </div>
  );
}
